import { create } from 'zustand';
import { creditsService, type CreditsInfo } from '@/services/credits.service';
import { userService } from '@/services/user.service';
import { isRequestCancelled } from '@/utils/request.utils';
import type { UserProfileState } from '@/types/user.types';

interface UserState {
  profile: UserProfileState | null;
  credits: CreditsInfo | null;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
}

interface UserActions {
  setProfile: (profile: UserProfileState | null) => void;
  fetchProfile: (signal?: AbortSignal) => Promise<UserProfileState | null>;
  updateProfile: (data: Partial<Pick<UserProfileState, 'displayName' | 'schoolName' | 'gradeLevels' | 'subjects'>>, signal?: AbortSignal) => Promise<void>;
  fetchCredits: (signal?: AbortSignal) => Promise<void>;
  clearUser: () => void;
}

type UserStore = UserState & UserActions;

const initialState: UserState = {
  profile: null,
  credits: null,
  loading: false,
  profileLoading: false,
  error: null,
};

export const useUserStore = create<UserStore>((set, get) => ({
  ...initialState,

  setProfile: (profile) => {
    set({ profile });
  },

  fetchProfile: async (signal?: AbortSignal) => {
    set({ profileLoading: true, error: null });

    try {
      const apiProfile = await userService.getUserProfile(signal);

      // Check if request was aborted
      if (signal?.aborted) return null;

      const profile: UserProfileState = {
        uid: apiProfile.uid,
        email: apiProfile.email,
        displayName: apiProfile.displayName,
        schoolName: apiProfile.schoolName,
        gradeLevels: apiProfile.gradeLevels || [],
        subjects: apiProfile.subjects || [],
        onboardingCompleted: apiProfile.onboardingCompleted,
      };

      set({ profile, profileLoading: false });
      return profile;
    } catch (error) {
      // Ignore aborted requests
      if (isRequestCancelled(error)) {
        return null;
      }

      const message = error instanceof Error ? error.message : 'Failed to fetch profile';
      set({ error: message, profileLoading: false });
      throw error;
    }
  },

  updateProfile: async (data, signal?: AbortSignal) => {
    set({ profileLoading: true, error: null });

    try {
      await userService.updateProfile(data, signal);

      // Check if request was aborted
      if (signal?.aborted) return;

      // Update local state
      const currentProfile = get().profile;
      if (currentProfile) {
        set({
          profile: {
            ...currentProfile,
            ...data,
          },
          profileLoading: false,
        });
      } else {
        set({ profileLoading: false });
      }
    } catch (error) {
      // Ignore aborted requests
      if (isRequestCancelled(error)) {
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to update profile';
      set({ error: message, profileLoading: false });
      throw error;
    }
  },

  fetchCredits: async (signal?: AbortSignal) => {
    // Don't fetch if already loading
    if (get().loading) return;

    set({ loading: true, error: null });

    try {
      const credits = await creditsService.getCredits(signal);

      // Check if request was aborted
      if (signal?.aborted) return;

      set({ credits, loading: false });
    } catch (error) {
      // Ignore aborted requests
      if (isRequestCancelled(error)) {
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to fetch credits';
      set({ error: message, loading: false });
    }
  },

  clearUser: () => {
    set(initialState);
  },
}));
